import { EvaluationColor } from '@repo/types';
import c from 'classnames';
import { useMemo } from 'react';
import styles from './RangeBar.module.css';
import RangeSegment from './RangeSegment';
import {
  computeColoredBarStyles,
  prepareRanges,
  RangeBarRange,
} from './utils';

type Props = {
  ranges: RangeBarRange[];
  value: number;
  min?: number;
  max?: number;
  showValue?: boolean;
  valueDisplay?: string;
  formatBreakpoint?: (value: number) => string;
  /**
   * 'result' hides range-bound labels and the center-out fill, showing only the
   * user's result above the marker (used on the conditions detail page).
   */
  variant?: 'default' | 'result';
};

export function RangeBar({
  ranges,
  value,
  min,
  max,
  showValue = false,
  valueDisplay,
  formatBreakpoint,
  variant = 'default',
}: Props) {
  const preparedRanges = useMemo(
    () => prepareRanges({ ranges, value, min, max, formatBreakpoint }),
    [ranges, value, min, max, formatBreakpoint],
  );

  if (!preparedRanges?.length) return null;

  const valueMarkerColor: EvaluationColor =
    (showValue &&
      value !== 0 &&
      preparedRanges.find(r => r.isSampleRange)?.evaluation_color) ||
    'grey';

  const maxRangeUpper =
    preparedRanges[preparedRanges.length - 1].range_upper;

  return (
    <div
      className={c(
        styles.barContainer,
        showValue && valueDisplay && styles.barExtraMargin,
      )}
    >
      <div className={styles.barWrapper}>
        {preparedRanges.map((range, idx) => (
          <RangeSegment
            key={`${range.range_lower}_${range.range_upper}`}
            range={range}
            showValue={showValue}
            valueDisplay={valueDisplay}
            valueMarkerColor={valueMarkerColor}
            isFirst={idx === 0}
            isLast={idx === preparedRanges.length - 1}
            variant={variant}
          />
        ))}
        {variant !== 'result' && (
          <span
            className={styles.coloredBar}
            style={computeColoredBarStyles(
              value,
              maxRangeUpper,
              valueMarkerColor,
            )}
          />
        )}
      </div>
    </div>
  );
}
